/**
 * PEOPLE AND SIGN-IN, READ AS THE DOOR READS IT (slice A3): what she types for a person to add, or
 * for her own new details, checked by the rules the request door applies before it takes them
 * (`api/src/lib/identifier.ts`, `normaliseIdentifier`), so a sheet says what to fix before it sends
 * and never sends what the door would refuse; and where each of her people asks stands, in words.
 *
 * Nothing typed is compared with any address or number home holds: that would confirm another
 * person's address (ops's contract, section 3, `household.people`). The door normalises what is sent
 * itself, so this decides only whether it would.
 */
import type { PeopleAsk } from '../api.ts';
import { ANSWERED, FAILED_BEFORE_PHONE, REQUEST } from '../words.ts';
import { normaliseIdentifier } from './identifier.ts';

/** E.164: a plus, then eight to fifteen digits, the first of them never zero. */
const E164 = /^\+[1-9][0-9]{7,14}$/;

/**
 * An address as the door takes one, or null for one it would refuse: read by the sign-in page's own
 * copy of the Worker's rule (`lib/identifier.ts`), so trimmed, lower-cased and its domain in ASCII,
 * and a sheet takes `k.morgan@bücher.de` as sign-in does. `api/test/identifier.test.ts` holds the
 * two to the Worker's reading over the same entries.
 */
export function typedEmail(typed: string): string | null {
  const read = normaliseIdentifier(typed);
  return read?.channel === 'email' ? read.value : null;
}

/**
 * A number as the door takes one, in E.164, or null for one it would refuse: ten digits, eleven
 * with the country code in front, or E.164, written with spaces, dots, dashes and brackets as people
 * write them. Never an address.
 */
export function typedMobile(typed: string): string | null {
  if (typed.includes('@')) return null;
  const compact = typed.replace(/[\s().-]/g, '');
  if (E164.test(compact)) return compact;
  if (/^[0-9]{10}$/.test(compact)) return `+1${compact}`;
  if (/^1[0-9]{10}$/.test(compact)) return `+${compact}`;
  return null;
}

/**
 * What a line says of her ask: words, with the phone after them where `phone` is set; or ops's own
 * sentence for `409 last_client`, which the screen says with the office's number as a link.
 */
export type AskSaid =
  { readonly text: string; readonly phone?: true } | { readonly lastClient: true };

/**
 * Where her people ask stands, in words, as Home says where an edit of a home stands, or null where
 * there is nothing to say:
 *
 * - `sent`, and then ops's `received`, "Sent to the office." and "Waiting for us to confirm.";
 * - ops's decision in the office's own words, or, declined with none, that the office could not do
 *   it, with the phone, from ops's own state (`ANSWERED`, round six, slice S2);
 * - withdrawn, as Home says it; any other closing, nothing;
 * - ops's `last_client`, its own sentence (section 12.5); any other refusal, and an ask that ran out
 *   of attempts, that it did not go through, with the phone.
 *
 * Never that it was done: one ops accepted is not given to the screen at all (binding rule 1).
 */
export function askLine(ask: PeopleAsk): AskSaid | null {
  switch (ask.state) {
    case 'sent':
      return { text: REQUEST.sent };
    case 'received':
      return { text: REQUEST.waiting };
    case 'declined':
      return ask.answer === undefined
        ? { text: ANSWERED.notDoneBeforePhone, phone: true }
        : { text: ask.answer };
    case 'cancelled':
      return ask.reason === 'withdrawn' ? { text: REQUEST.withdrawn } : null;
    case 'refused':
      return ask.code === 'last_client'
        ? { lastClient: true }
        : { text: FAILED_BEFORE_PHONE, phone: true };
    case 'stopped':
      return { text: FAILED_BEFORE_PHONE, phone: true };
    default:
      return null;
  }
}
